'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { buildFiltersFromParams } from '../utils/exploreState';

/**
 * Hook to sync explore state with URL query parameters
 *
 * Supported params:
 * - view: 'map' | 'list'
 * - community: community slug (e.g., 'blue-springs')
 * - types: comma-separated types (e.g., 'events,attractions')
 * - search: search query string
 *
 * Example URL: /explore?view=list&community=blue-springs&types=events,attractions
 */
export function useExploreUrlParams(dispatch, setFilters, setViewMode, initialMarkers, t0, t1) {
  const $ = _c(13);
  const attractionCategories = t0 === undefined ? null : t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isHydrated: t3,
    initialUrlParamsKey: t4,
    suppressUrlSyncRef
  } = t2;
  const isHydrated = t3 === undefined ? false : t3;
  const initialUrlParamsKey = t4 === undefined ? null : t4;
  const searchParams = useSearchParams();
  const prevParamsRef = useRef(null);
  let t5;
  let t6;
  if ($[2] !== attractionCategories || $[3] !== dispatch || $[4] !== initialMarkers || $[5] !== initialUrlParamsKey || $[6] !== isHydrated || $[7] !== searchParams || $[8] !== setFilters || $[9] !== setViewMode || $[10] !== suppressUrlSyncRef) {
    t5 = () => {
      if (!isHydrated) {
        return;
      }
      if (initialMarkers.length === 0) {
        return;
      }
      const view = searchParams.get("view");
      const community = searchParams.get("community");
      const types = searchParams.get("types");
      const search = searchParams.get("search");
      const promo = searchParams.get("promo");
      const attr = searchParams.get("attr");
      const showClusters = searchParams.get("showClusters");
      const fadeIcons = searchParams.get("fadeIcons");
      const currentParams = `${view}|${community}|${types}|${search}|${promo}|${attr}|${showClusters}|${fadeIcons}`;
      if (prevParamsRef.current === currentParams) {
        return;
      }
      if (prevParamsRef.current === null && initialUrlParamsKey === currentParams) {
        prevParamsRef.current = currentParams;
        return;
      }
      prevParamsRef.current = currentParams;
      if (suppressUrlSyncRef?.current !== undefined) {
        suppressUrlSyncRef.current = true;
      }
      if (view && ["map", "list"].includes(view)) {
        setViewMode(view, {
          syncUrl: false
        });
      } else {
        if (!view && (community || types)) {
          setViewMode("list", {
            syncUrl: false
          });
        }
      }
      if (community || types || promo || attr) {
        const newFilters = buildFiltersFromParams(community, types, promo, attr, initialMarkers, attractionCategories);
        if (newFilters) {
          setFilters(newFilters);
        }
      } else {
        const allCommunityIds = initialMarkers.filter(_temp).map(_temp2);
        const allAttractionIds = attractionCategories?.filterOptions ? attractionCategories.filterOptions.map(_temp3) : initialMarkers.filter(_temp4).map(_temp5);
        setFilters({
          hearts: true,
          artists: true,
          events: true,
          communities: {
            all: true,
            selected: allCommunityIds
          },
          attractions: {
            all: true,
            selected: allAttractionIds
          },
          promotions: {
            all: true,
            dining: true,
            attractions: true,
            sports: true,
            arts: true
          }
        });
      }
      if (search) {
        dispatch({
          type: "SET_SEARCH_QUERY",
          payload: search
        });
        dispatch({
          type: "SET_SEARCH_COMMITTED",
          payload: true
        });
      } else {
        dispatch({
          type: "SET_SEARCH_QUERY",
          payload: ""
        });
        dispatch({
          type: "SET_SEARCH_COMMITTED",
          payload: false
        });
      }
      if (showClusters) {
        dispatch({
          type: "SET_CLUSTERING",
          payload: showClusters === "true"
        });
      }
      if (fadeIcons) {
        dispatch({
          type: "SET_FADE_ICONS",
          payload: fadeIcons === "true"
        });
      }
    };
    t6 = [searchParams, dispatch, setFilters, setViewMode, initialMarkers, attractionCategories, isHydrated, initialUrlParamsKey, suppressUrlSyncRef];
    $[2] = attractionCategories;
    $[3] = dispatch;
    $[4] = initialMarkers;
    $[5] = initialUrlParamsKey;
    $[6] = isHydrated;
    $[7] = searchParams;
    $[8] = setFilters;
    $[9] = setViewMode;
    $[10] = suppressUrlSyncRef;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
}

/**
 * Get the active community name from filters
 * Returns the community name if exactly one community is selected, null otherwise
 */
function _temp5(m_2) {
  return m_2.id;
}
function _temp4(m_1) {
  return m_1.type === "attraction";
}
function _temp3(opt) {
  return opt.value;
}
function _temp2(m_0) {
  return m_0.id;
}
function _temp(m) {
  return m.type === "community";
}
export function getActiveCommunityName(filters, markers) {
  const selectedCommunities = filters.communities?.selected || [];

  // Only show community name if exactly one is selected and "all" is off
  if (filters.communities?.all || selectedCommunities.length !== 1) {
    return null;
  }
  const communityId = selectedCommunities[0];
  const communityMarker = markers.find(m => m.id === communityId && m.type === 'community');
  return communityMarker?.name || null;
}